import { Button, Col, message, Modal, Row, Typography } from "antd";
import React, { useState } from "react";
import OtpInput from "react-otp-input";
import { httpRequest } from "../helpers/api";
import { BaseResponseProps } from "../types/config.type";
import { getErrorMessage } from "../helpers/errorHandler";
import useAuth from "../hooks/useAuth";

interface IProps {
	open: boolean;
	onClose: () => void;
	type: "agent" | "office";
	onSuccess?: () => void;
	data: {
		email: string;
		officeId?: string;
		employeeId?: string;
		portalId: string;
	};
}
export default function PortalProfileOTPModal({ open, onClose, data, onSuccess, type }: IProps) {
	const [otp, setOtp] = useState("");
	const [showVerificationOTP, setShowVerificationOTP] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [sessionId, setSessionId] = useState("");
	const { isHQ } = useAuth();

	const { officeId, email, portalId, employeeId } = data;

	const endpoint = type === "agent" ? "agent-portal-profiles" : "office-portal-profiles";

	const sendOtp = async () => {
		setIsLoading(true);

		try {
			let dto: any = {
				email,
			};

			if (officeId) {
				dto = {
					...dto,
					officeId,
				};
			}

			if (employeeId) {
				dto = {
					...dto,
					employeeId,
				};
			}

			const res = await httpRequest.post<BaseResponseProps<{ sessionId: string; otp: string; autoApproved?: boolean }>>(
				`${endpoint}/auth/send-otp`,
				dto
			);
			if (res.data.payload?.sessionId) {
				setSessionId(res.data.payload.sessionId);
				
				// If auto-approved, directly create profile
				if (res.data.payload.autoApproved) {
					await createProfile(res.data.payload.sessionId);
				} else {
					setShowVerificationOTP(true);
					if (process.env.REACT_APP_STAGE !== "production") {
						setOtp(res.data.payload.otp);
					}
				}
			}
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const createProfile = async (sessionIdParam: string) => {
		const payloadAgent = {
			portalId,
			portalUsername: email,
			employeeId,
			sessionId: sessionIdParam,
			originUrl: window.location.href,
		};

		const payloadOffice = {
			portalId,
			officeId,
			portalUsername: email,
			sessionId: sessionIdParam,
			originUrl: window.location.href,
		};

		await httpRequest.post(
			`${isHQ ? "" : "/office"}/${endpoint}`,
			type === "agent" ? payloadAgent : payloadOffice
		);

		setOtp("");
		setShowVerificationOTP(false);
		setSessionId("");
		onClose();
		onSuccess?.();
	};

	const verifyOtp = async () => {
		if (!otp) {
			return message.error("OTP can't be empty");
		}

		setIsLoading(true);
		try {
			const res = await httpRequest.post<BaseResponseProps<{ isSuccess: boolean }>>(
				`${endpoint}/auth/verify-otp`,
				{
					sessionId,
					otp,
				}
			);
			if (res.data.payload.isSuccess) {
				await createProfile(sessionId);
			}
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Modal
				open={open}
				onCancel={() => {
					onClose();
				}}
				destroyOnClose={true}
				width={520}
				footer={null}
			>
				<Typography.Title
					level={4}
					style={{ textAlign: "center", marginTop: 16 }}
				>
					Verification OTP
				</Typography.Title>
				<Typography.Text style={{ textAlign: "center", display: "block", marginBottom: 16, color: "#838e96" }}>
					You need to verify your OTP first. we will send your OTP to {email}
				</Typography.Text>
				<Row gutter={[12, 12]}>
					<Col span={12}>
						<Button
							block
							loading={isLoading}
							onClick={() => {
								onClose();
							}}
						>
							Cancel
						</Button>
					</Col>
					<Col span={12}>
						<Button
							block
							type="primary"
							loading={isLoading}
							onClick={sendOtp}
						>
							Send OTP
						</Button>
					</Col>
				</Row>
			</Modal>

			<Modal
				title={"Enter Verification OTP"}
				open={showVerificationOTP}
				onCancel={() => {
					setShowVerificationOTP(false);
					setOtp("");
				}}
				destroyOnClose={true}
				width={520}
				footer={null}
			>
				<OtpInput
					containerStyle={{
						width: "100%",
					}}
					inputStyle={{
						width: "100%",
						height: 50,
						fontSize: 16,
					}}
					value={otp}
					onChange={setOtp}
					numInputs={6}
					renderSeparator={<span>-</span>}
					renderInput={(props) => <input {...props} />}
				/>
				<div style={{ margin: "16px 0px" }}>
					<Typography.Text
						type="warning"
						style={{ fontSize: 12 }}
					>
						* The OTP has been sent to {email}. Check your inbox or spam folder, it may take up to 5 minutes
						to arrive.
					</Typography.Text>
				</div>
				<Button
					loading={isLoading}
					disabled={otp.length < 6}
					type="primary"
					block
					onClick={verifyOtp}
				>
					Verify OTP
				</Button>
			</Modal>
		</>
	);
}
